'use client';

/**
 * 全局错误边界 — v6.97.62 P0 FIX 2026-08-03 老板报
 * Mac Safari / iPad Safari / Windows Chrome 进 /dongniai/ 显示:
 *   "Application error: a client-side exception has occurred while loading www.xiaoquanwk.com"
 * Next.js 默认 error overlay 英文 + 只显示 console 提示
 * 改成中文友好 UI + 自动 fetch /dongniai_client_error.php 落盘到 /tmp/dongniai-client-errors.log
 *   (绕开 next.js rebuild, PHP 端点立即生效)
 *
 * 注意: global-error.tsx 必须包含自己的 <html><body>, 因为它替换整个 root layout.
 *       (Next.js 16 App Router 约定, 见 https://nextjs.org/docs/app/api-reference/file-conventions/error)
 *
 * 【v6.97.62+3 P0 FIX 2026-08-03 老板报】Build 错误修复
 * 真因: 之前用 <button onClick={() => reset()}> 在 JSX 直接写 inline function,
 *       Next.js 16 build prerender 阶段 serialize client component tree 时,
 *       onClick function 不能 stringify, 报 "Event handlers cannot be passed
 *       to Client Component props". 在 /knowledge-graph / /compare/... 等所有
 *       8 个 SEO page build 时都触发 (因为每个 page 加载整棵 component tree
 *       做 prerender).
 * 修法: 删所有 onClick inline function, 改用 useRef + useEffect 挂载后
 *       addEventListener 绑定 reset. 这样 JSX 里没有任何 inline function,
 *       build prerender 序列化时不会触发 stringify 错.
 */

import { useEffect, useRef } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // 落盘到 server log (PHP 端点 + 已有 client-error-tracker.js 兜底, 双保险)
    try {
      fetch('/dongniai_client_error.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'global-error-boundary',
          message: error?.message || 'unknown',
          stack: error?.stack || '',
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          digest: error?.digest || '',
          file: '',
          line: '',
          col: '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      // 失败吞掉, 不能让 error reporter 自己让页面更糟
    }

    // 用 useEffect 挂载后绑定 reset button, 避免 JSX 写 onClick inline function
    const btn = resetButtonRef.current;
    if (btn) {
      const handler = (e: MouseEvent) => {
        e.preventDefault();
        try {
          reset();
        } catch (e) {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }
      };
      btn.addEventListener('click', handler);
      return () => {
        btn.removeEventListener('click', handler);
      };
    }
  }, [error, reset]);

  return (
    <html lang="zh-CN">
      <body
        style={{
          margin: 0,
          fontFamily: 'system-ui, -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif',
          background: '#fafafa',
          color: '#0f172a',
        }}
      >
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: 480,
              width: '100%',
              background: '#fff',
              borderRadius: 16,
              padding: '32px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #ef4444, #f97316)',
                borderRadius: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32,
                color: '#fff',
              }}
            >
              ⚠️
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 8px', color: '#0f172a' }}>
              页面遇到了一个问题
            </h1>
            <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 20px', lineHeight: 1.6 }}>
              抱歉，页面加载时遇到异常。我们已经自动记录了这个错误，工程师会尽快修复。
            </p>
            {error?.message && (
              <details
                style={{
                  marginBottom: 20,
                  padding: 12,
                  background: '#f1f5f9',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#475569',
                  textAlign: 'left',
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                  wordBreak: 'break-all',
                }}
              >
                <summary style={{ cursor: 'pointer', fontWeight: 500, marginBottom: 4 }}>
                  技术细节
                </summary>
                <div style={{ marginTop: 8, lineHeight: 1.5 }}>
                  <div><strong>message:</strong> {error.message}</div>
                  {error.digest && <div style={{ marginTop: 4 }}><strong>digest:</strong> {error.digest}</div>}
                </div>
              </details>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                ref={resetButtonRef}
                type="button"
                style={{
                  padding: '10px 20px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                }}
              >
                🔄 重试
              </button>
              <a
                href="/dongniai/"
                style={{
                  padding: '10px 20px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#475569',
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                🏠 返回首页
              </a>
            </div>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '20px 0 0' }}>
              如果问题持续，请截图发客服微信。我们会立即排查。
            </p>
          </div>
        </div>
      </body>
    </html>
  );
}
